import React from "react";
import { Link } from "react-router-dom";

import { Calculator, Rocket } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import CTAButtonLink from "../CTA/CTAButtonLink.jsx";
import Seo from "../Seo.jsx";
import Tooltip from "./Tooltip";
import guidesConfig from "../../config/guidesConfig";

import "./guides.css";

const ScamGuide = () => {
  const meta = guidesConfig.ScamGuide?.seo || {};

  return (
    <div className="guides-content">
      <Seo
        title={meta.title}
        description={meta.description}
        url={guidesConfig.scamGuide?.route || "/guides"}
      />

      <section>
        <h2>
          First, What{" "}
          <Tooltip content="Matched betting is a method where you use bookmaker promotions to secure a profit by covering every outcome of a sports event.">
            Is Matched Betting?
          </Tooltip>
        </h2>
        <p>
          Matched betting is a strategy where you use bookmaker promotions (like free bets or bonuses) to lock in a profit by covering
          all outcomes of a sports event. You place a{" "}
          <strong>
            <Tooltip content="A back bet is a wager placed on an outcome to occur at a traditional bookmaker.">
              back bet
            </Tooltip>
          </strong>{" "}
          at the bookie (for example, betting on Team A to win), and then place a{" "}
          <strong>
            <Tooltip content="A lay bet is a wager placed at a betting exchange on the outcome not to occur.">
              lay bet
            </Tooltip>
          </strong>{" "}
          at a betting exchange (betting on Team A not to win). The two bets cancel each other out, leaving you with a small qualifying loss or, when using a free bet, a guaranteed profit.
        </p>
        <h6 className="info">
          Key Takeaway: It’s legal, works reliably, and is based on proven math rather than chance.
        </h6>
      </section>

      <section>
        <h2>No, it’s not a scam</h2>
        <p>
          Matched betting is straightforwardly <strong>not</strong> a scam. You’re
          simply using your own accounts, your own bets, and mathematical principles that have been proven over decades.
        </p>
        <p>It's frequently featured in major papers like <a href="https://www.theguardian.com/money/2010/jul/24/free-bets-bookies" target="_blank" rel="noopener nofollow noreferrer">The Guardian</a> and <a href="https://www.telegraph.co.uk/money/consumer-affairs/matched-betting-gambling-site-loophole-make-money/" target="_blank" rel="noopener nofollow noreferrer">The Telegraph</a>.</p>
        <p>
          Thousands of everyday people all use matched betting successfully, and have done for decades.
        </p>
      </section>

      <CTALarge 
        to="/calculators/matched-betting" 
        text="Go to Matched Betting Calculator" 
        icon={Calculator} 
      />

      <section>
        <h2>Matched Betting: What's the catch?</h2>


        <article>
          <h3>1. "It’s Not Passive Income"</h3>
          <p>
            People often mention matched betting in the same sentence as “passive income”. Definitely not true.
          </p>
          <p>
            You’ll be:
          </p>
          <ul>
            <li>Checking daily offers</li>
            <li>Comparing odds between bookies and exchanges</li>
            <li>Tracking your bets, profit and losses</li>
          </ul>
          <p>
            It gets quicker with experience, and tools help – but it still needs attention. If you want true passive income, this ain’t it.
          </p>
          <h6 className="warn"><strong>Verdict:</strong> Definitely true, matched betting is not passive. It's more like freelance work. It does get much faster the longer you do it, though.</h6>
        </article>
        <article>
          <h3>2. "You Need a Massive Bankroll"</h3>
          <p>
            To do matched betting, you place two bets: one at a bookmaker, and one at a betting exchange (like Betfair or Smarkets). 
            To cover both, you’ll need money in both accounts. Most beginners start with around £100–£300 to keep things moving.
          </p>
          <p>But why? Well...</p>
          <p>
            The usual point at which beginners get a scare is when you place a <strong className="lay">lay bet</strong> (a bet <em>against</em> something happening) on the exchange: 
            they don’t just take the stake – they hold onto a bigger chunk of money, called the <Tooltip content="The amount you risk losing when placing a lay bet">liability</Tooltip>.
          </p>
          <p>
            Why? Because if your lay bet loses, they need to pay out the winnings to the person who matched your bet. 
            So the exchange “freezes” the amount you would owe if you lose.
          </p>
          <p><strong>If the lay bet wins, you'll get it back. If you lose, you win at the bookie and it covers your loss.</strong></p>
          <p>
            For example, if you lay £10 at odds of 5.0 (4/1), your liability is £40 – that’s the amount the exchange will hold in your account, 
            just in case the bet loses. You’re not risking it (it's matched at the bookie), but it is temporarily tied up.
          </p>
          <p>
            So, if you want to do multiple offers at once, you’ll need more funds ready to go.
          </p>
          <p>
            If you're tight on cash or uncomfortable seeing money “locked away” for a bit, matched betting might feel stressful. 
            Better to know that upfront than panic later.
          </p>
          <h6 className="warn"><strong>Verdict:</strong> You do need an absolute minimum of £50 to start with. Unless you have a couple of hundred, early progress will feel slow. The more you have, the more you can make (up to a point).</h6>

        </article>


        <article id="mistakes">
          <h3>4. Mistakes Can Cost You</h3>
          <p>
            Going to be brutally honest. Precision matters. Entering wrong odds, forgetting to match a bet, or miscalculating your stake can result in actual losses.
          </p>
          <p>
            <strong>But</strong> – an important <em>but</em> – there are people on the net saying wild things like a single mistake could wipe out <a href="https://lifehacker.com/money/why-matched-betting-is-a-terrible-idea" target="_blank" rel="noopener nofollow noreferrer">"months of profit"</a> (whoever wrote this one has very obviously never done matched betting and doesn't understand how it works), so therefore, they say, it's not worth the risk.
          </p>
          
          <p>This is basically rubbish, and it's frustrating that people who don't understand matched betting say this with such confidence.</p>
          <p>Let me explain what mistakes might look like in real life.</p>
          <p>
            Let’s say you’re doing a simple offer: bet £10, get a £10 free bet. You place the £10 <span className="back">back</span> bet, but forget to <span className="lay">lay</span> it on the exchange. The worst-case? You lose that £10 stake. Though you still qualify for the free bet and make most of it back in that.
            It’s annoying, sure. But it's not the end of the world, and it certainly doesn’t "wipe out months" of earnings.
          </p>
          <p>
            Here’s a worse mistake: you <strong>place a <span className="lay">lay</span> bet but forget the <span className="back">back</span> bet</strong>. This is a proper facepalm moment, and yes, it can cost you.
          </p>
          <p>
            Let’s say you lay £10 on a team to <em>not</em> win at odds of 5.0. You’re expecting to also back the same team at a bookmaker to complete the match. 
            But for whatever reason – maybe you got distracted or thought you’d already done it – you never place the back bet.
          </p>
          <p>
            Now the match kicks off, and guess what? That team wins. Your lay bet <em>loses</em> at the exchange, and you owe the full liability – in this case, £40. 
            And because you didn’t place the back bet, there’s nothing on the other side to cover the loss. That’s money straight out of your pocket.
          </p>
          <p>
            Is it painful? Yep. Is it recoverable? Also yes – most people only make this kind of mistake once. You learn fast when real money’s involved.
          </p>
          <p>Of course, mistakes can go in your favour. Anecdotally, when I was first matched betting, in the first 3 months I made £211 in mistakes on the road to £4000 profit (I had a lot of time on my hands), <em>but</em> because some of them went in my favour, the net loss was £9. Not guaranteed, of course. You could be unlucky. Or luckier.</p>
          <h6 className="warn"><strong>Verdict:</strong> True. But you're still overwhelmingly likely to make overall profit. </h6>
        </article>

        <article>
  <h3>
    1. You Can Get Banned
  </h3>
  <p>
    It does happen. The honest answer is: not usually. 
    What’s far more common is getting <span className="key">gubbed</span>.
  </p>
  <p>
    Gubbing means the bookmaker cuts you off from promotions. No more free bets. No more odds boosts. No more easy profit from that bookie. 
    You can still log in, place bets, withdraw your money – but not much else.
  </p>
  <p>
    Sometimes they go further and <span className="key">stake restrict</span> you – limiting your bets to tiny amounts. 
    And yes, in rare-ish cases, if you’ve really hammered them, they might ban your account altogether.
  </p>
  <h6 className="info">
  But this is important: even if you get gubbed or banned, your money is safe. As long as you're betting with a licensed UK bookmaker, 
  your funds are protected and you’ll still be able to withdraw whatever’s in your account.
</h6>
  <p>
    Gubbing isn’t random. If you take profit, you'll eventually be gubbed from at least some of your accounts. If you rinse every promo, bet like a robot, and never act like a “normal” punter, 
    you’ll be flagged faster. Gubbing is part of the game.
  </p>
  <p>There are strategies to keep profit coming in from gubbed accounts, too, mostly more advanced.</p>
  <h6 className="warn"><strong>Verdict:</strong> Kind of true. But by then, the profit's been made, so do you care?</h6>

</article>

<h3>3. "You're Still Losing Money on Some Bets"</h3>
<p>
  This one comes up a lot, especially from people who haven't actually tried matched betting. They see a bet where you "lose £1" and assume you've just wasted money. But that small loss is actually how matched betting works (and that money is definitely not being <a href="https://lifehacker.com/money/why-matched-betting-is-a-terrible-idea" target="_blank" rel="noopener nofollow noreferrer">"risked"</a>).
</p>
<p>
  It's called a <span class="key">qualifying loss</span> — and it's not a real loss in the big picture. You’re placing that bet to unlock a free bet or bonus. That tiny hit (usually 10p - £1) is just the cost of "entry".
</p>
<p>
  Think of it like buying a discounted voucher. You spend £50p to get a £10 free bet — which you then convert into £7–£8 real profit using <Link to={"/guides/free-bets-guide"}>the same method</Link>.
</p>
<p>
  Let's say ou place a £10 qualifying <span className="back">back</span> bet on Man Utd at 2.0 and <span className="lay">lay</span> it at 2.02. After the match, you’re down £0.10 because of the odds difference — that’s the qualifying loss. But now you’ve got a £10 free bet. You use that on a team at decent odds and lock in £8 guaranteed. Final profit = £7.90.
</p>
<p>
  So yes, you “lose” on the qualifying bet — but that’s just step one. It's the setup for the actual profit. If you understand that, you’re halfway to mastering matched betting.
</p>

<h6 class="info">
  Qualifying losses should always be small. If you’re seeing bigger losses, double-check your odds. Always use a <a href="/calculators/lay-stake-calculator">matched betting calculator</a>. To get the closest matches and maximise profit, <Link to={"/guides/oddsmonkey-guide"}>consider using OddsMonkey</Link>.
</h6>

<h6 className="warn">
  Verdict: Technically true, but deeply misleading. Qualifying losses are just the trade-off for unlocking much bigger profits. It’s like putting a coin in a vending machine that spits out a fiver.
</h6>

    
        <article>
          <h3>5. It Slows Down Over Time</h3>
          <p>
            When you first start, it’s “print money mode”. Bookies throw sign-up offers at you, and profits stack up fast. 
            But once you’ve done all the major welcome offers, things chill out. Reload offers – the ones for existing customers – are smaller. 
          </p>
          <p>
            That doesn’t mean it’s game over. Plenty of matched bettors earn £100–£700 a month just from reloads.
          </p>
          <p>
            <strong>Tip:</strong> If you want to keep earning long-term, <Link to={"/guides/oddsmonkey-guide"}>OddsMonkey</Link> is the best-loved complete matched betting platform. It highlights offers and lets you access many other avenues for profit. We love it.
          </p>
          <h6 className="warn"><strong>Verdict:</strong> It does slow down, but by then you should be confident with some more advanced strategies to keep profit coming in. Or, take the money and run.</h6>

        </article>

        <article>
          <h3>6. Bookmakers Can Be Painful</h3>
          <p>
            Yes, bookies <em>can</em> occasionally be a nightmare. Looking at you, 888Sport. You’ll deal with:
          </p>
          <ul>
            <li>Odds changes right before you bet (frequently)</li>
            <li>Missing free bets (Most bookies automate this but you'll have the odd trip to live chat with others)</li>
<li>The occasional wild verification process ("take a selfie in front of your house with your ID and a piece of paper with today's date" stands out)</li>
            <li>Annoying live chat</li>
            <li>Slow withdrawals (But rare in the UK)</li>
          </ul>
          <p>
            It’s all part of the territory. After all, you’re not their favourite type of customer.
          </p>
          <h6 className="warn"><strong>Verdict:</strong> Yeah, a minority can be annoying for sure.</h6>

        </article>

        <section>
          <h2>Final Thoughts</h2>
          <p>
            Matched betting is a proven method to earn extra income, as long as you approach it the right way.
            You won’t become rich overnight, but it can help pay for a holiday, manage debt, or build up some extra savings over time.
          </p>
        </section>

        <section>
          <h2>Want to Start Matched Betting?</h2>
        </section>
        <CTAButtonLink 
  to="/get-started/step1"
  text="Understand How Matched Betting Works in 180 Seconds"
  buttonText="Start now"
  icon={Rocket}
  tabText="Popular"
/>
      </section>
    </div>
  );
};

export default ScamGuide;
